import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
// Use the same DualEditor as Product Full Description
import DualEditor from './DualEditor';
import { uploadAPI } from '../../services/api';
import { 
  Mail, 
  Users, 
  Send, 
  Download, 
  Upload, 
  Plus, 
  Edit, 
  Trash2, 
  BarChart3,
  Search,
  Loader2,
  CheckSquare,
  Square,
  Zap
} from 'lucide-react';
import { newsletterAPI } from '../../services/api';
import { useNotificationHook } from '../../hooks/useNotificationHook';

const NewsletterManagement = () => {
  const { notify } = useNotificationHook();
  const [activeTab, setActiveTab] = useState('subscribers');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({});
  const [subscribers, setSubscribers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Modal states
  const [showAddSubscriber, setShowAddSubscriber] = useState(false);
  const [showSendEmail, setShowSendEmail] = useState(false);
  const [showBulkEmail, setShowBulkEmail] = useState(false);
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  
  // Bulk actions state
  const [selectedSubscribers, setSelectedSubscribers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Form states
  const [subscriberForm, setSubscriberForm] = useState({
    email: '',
    name: '',
    notes: ''
  });

  const [emailForm, setEmailForm] = useState({
    subject: '',
    content: ''
  });

  const [bulkEmailForm, setBulkEmailForm] = useState({
    subject: '',
    content: ''
  });

  // Defaults modal state
  const [showDefaults, setShowDefaults] = useState(false);
  const [defaultsForm, setDefaultsForm] = useState({
    companyTitle: '',
    companyTagline: '',
    mainTitle: '',
    footerText: '',
    footerTagline: '',
    unsubscribeText: '',
    social: {
      website: { text: '', url: '' },
      facebook: { text: '', url: '' },
      twitter: { text: '', url: '' },
      linkedin: { text: '', url: '' },
      instagram: { text: '', url: '' }
    }
  });
  const [savingDefaults, setSavingDefaults] = useState(false);

  // Remove custom editor and reuse DualEditor for consistency

  // Load initial data
  useEffect(() => {
    loadStats();
    loadSubscribers();
    loadDefaults();
  }, []);

  const loadStats = async () => {
    try {
      const response = await newsletterAPI.getStats();
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadDefaults = async () => {
    try {
      const res = await newsletterAPI.getDefaults();
      if (res.data.success && res.data.data) {
        setDefaultsForm(prev => ({ ...prev, ...res.data.data, social: { ...prev.social, ...(res.data.data.social || {}) } }));
      }
    } catch (_) {}
  };

  const saveDefaults = async () => {
    try {
      setSavingDefaults(true);
      // Try PUT first, then POST fallback (some hosts block PUT)
      let res;
      try {
        res = await newsletterAPI.updateDefaults(defaultsForm);
      } catch (_) {
        res = await newsletterAPI.saveDefaults(defaultsForm);
      }
      if (res.data.success) {
        notify.success('Defaults saved');
        await loadDefaults();
        setShowDefaults(false);
      } else {
        notify.error(res.data.message || 'Failed to save defaults');
      }
    } catch (e) {
      notify.error(e?.response?.data?.message || 'Failed to save defaults');
    } finally {
      setSavingDefaults(false);
    }
  };

  const loadSubscribers = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 20,
        search: searchTerm,
        status: statusFilter
      };
      
      const response = await newsletterAPI.getSubscribers(params);
      if (response.data.success) {
        setSubscribers(response.data.data.subscribers);
      }
    } catch (error) {
      notify.error('Failed to load subscribers');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubscriber = async () => {
    try {
      const response = await newsletterAPI.addSubscriber(subscriberForm);
      if (response.data.success) {
        notify.success('Subscriber added successfully');
        setShowAddSubscriber(false);
        setSubscriberForm({ email: '', name: '', notes: '' });
        loadSubscribers();
        loadStats();
      }
    } catch (error) {
      notify.error(error.response?.data?.message || 'Failed to add subscriber');
    }
  };

  const handleDeleteSubscriber = async (id) => {
    if (!window.confirm('Are you sure you want to delete this subscriber?')) return;
    
    try {
      const response = await newsletterAPI.deleteSubscriber(id);
      if (response.data.success) {
        notify.success('Subscriber deleted successfully');
        loadSubscribers();
        loadStats();
      }
    } catch (error) {
      notify.error('Failed to delete subscriber');
    }
  };

  const handleSendIndividualEmail = async () => {
    try {
      const response = await newsletterAPI.sendIndividualEmail({
        subscriber_id: selectedSubscriber.id,
        ...emailForm
      });
      if (response.data.success) {
        notify.success('Email sent successfully');
        setShowSendEmail(false);
        setEmailForm({ subject: '', content: '' });
        setSelectedSubscriber(null);
      }
    } catch (error) {
      notify.error(error.response?.data?.message || 'Failed to send email');
    }
  };

  const handleSendBulkEmail = async () => {
    try {
      const response = await newsletterAPI.sendBulkEmail(bulkEmailForm);
      if (response.data.success) {
        notify.success(`Bulk email campaign completed. ${response.data.data.sent_count} emails sent.`);
        setShowBulkEmail(false);
        setBulkEmailForm({ subject: '', content: '' });
      }
    } catch (error) {
      notify.error(error.response?.data?.message || 'Failed to send bulk email');
    }
  };

  const handleExport = async () => {
    try {
      const response = await newsletterAPI.exportSubscribers({ status: statusFilter });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'newsletter_subscribers.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      notify.success('Export completed successfully');
    } catch (error) {
      notify.error('Failed to export subscribers');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      active: 'bg-green-100 text-green-800',
      unsubscribed: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusClasses[status]}`}>
        {status}
      </span>
    );
  };

  // Bulk action functions
  const handleSelectSubscriber = (subscriberId) => {
    setSelectedSubscribers(prev => 
      prev.includes(subscriberId) 
        ? prev.filter(id => id !== subscriberId)
        : [...prev, subscriberId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedSubscribers([]);
      setSelectAll(false);
    } else {
      setSelectedSubscribers(subscribers.map(subscriber => subscriber.id));
      setSelectAll(true);
    }
  };

  const handleBulkStatusUpdate = async (status) => {
    if (selectedSubscribers.length === 0) return;
    
    try {
      setBulkActionLoading(true);
      const response = await newsletterAPI.bulkUpdateSubscriberStatus(selectedSubscribers, status);
      
      if (response.data.success) {
        notify.success(`Successfully updated ${selectedSubscribers.length} subscriber(s) status`);
        setSelectedSubscribers([]);
        setSelectAll(false);
        await loadSubscribers();
        await loadStats();
      } else {
        notify.error(response.data.message || 'Failed to update subscribers');
      }
    } catch (error) {
      console.error('Error updating subscribers:', error);
      notify.error('Error updating subscribers: ' + error.message);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedSubscribers.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedSubscribers.length} subscriber(s)?`)) {
      return;
    }
    
    try {
      setBulkActionLoading(true);
      const response = await newsletterAPI.bulkDeleteSubscribers(selectedSubscribers);
      
      if (response.data.success) {
        notify.success(`Successfully deleted ${selectedSubscribers.length} subscriber(s)`);
        setSelectedSubscribers([]);
        setSelectAll(false);
        await loadSubscribers();
        await loadStats();
      } else {
        notify.error(response.data.message || 'Failed to delete subscribers');
      }
    } catch (error) {
      console.error('Error deleting subscribers:', error);
      notify.error('Error deleting subscribers: ' + error.message);
    } finally {
      setBulkActionLoading(false);
    }
  };

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Newsletter Management</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage subscribers and email campaigns</p>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-4 sm:p-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 lg:p-4 sm:p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <Users className="h-5 w-5 lg:h-6 lg:w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-3 lg:ml-4">
              <p className="text-xs lg:text-sm font-medium text-gray-600 dark:text-gray-400">Total Subscribers</p>
              <p className="text-xl lg:text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{stats.total_subscribers || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 lg:p-4 sm:p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <Mail className="h-5 w-5 lg:h-6 lg:w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-3 lg:ml-4">
              <p className="text-xs lg:text-sm font-medium text-gray-600 dark:text-gray-400">Active Subscribers</p>
              <p className="text-xl lg:text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{stats.active_subscribers || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 lg:p-4 sm:p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <Send className="h-5 w-5 lg:h-6 lg:w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="ml-3 lg:ml-4">
              <p className="text-xs lg:text-sm font-medium text-gray-600 dark:text-gray-400">Total Campaigns</p>
              <p className="text-xl lg:text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{stats.total_campaigns || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 lg:p-4 sm:p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
              <BarChart3 className="h-5 w-5 lg:h-6 lg:w-6 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div className="ml-3 lg:ml-4">
              <p className="text-xs lg:text-sm font-medium text-gray-600 dark:text-gray-400">Avg Open Rate</p>
              <p className="text-xl lg:text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                {`${Number(stats?.avg_open_rate ?? 0).toFixed(1)}%`}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-4 sm:p-6">
          {/* Actions */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0 mb-6">
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
              <button
                onClick={() => setShowAddSubscriber(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Subscriber
              </button>
              <button
                onClick={() => setShowDefaults(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Defaults
              </button>
              <button
                onClick={() => setShowBulkEmail(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                <Send className="h-4 w-4 mr-2" />
                Send Bulk Email
              </button>
            </div>
            <button
              onClick={handleExport}
              className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search subscribers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="unsubscribed">Unsubscribed</option>
            </select>
            <button
              onClick={() => loadSubscribers()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Filter
            </button>
          </div>

          {/* Bulk Actions Bar */}
          {selectedSubscribers.length > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                    {selectedSubscribers.length} subscriber(s) selected
                  </span>
                  <button
                    onClick={() => {
                      setSelectedSubscribers([]);
                      setSelectAll(false);
                    }}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Clear selection
                  </button>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleBulkStatusUpdate('active')}
                    disabled={bulkActionLoading}
                    className="px-3 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded-md flex items-center disabled:opacity-50"
                  >
                    {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckSquare className="h-3 w-3 mr-1" />}
                    Activate
                  </button>
                  <button
                    onClick={() => handleBulkStatusUpdate('inactive')}
                    disabled={bulkActionLoading}
                    className="px-3 py-1 text-xs bg-yellow-600 hover:bg-yellow-700 text-white rounded-md flex items-center disabled:opacity-50"
                  >
                    {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Square className="h-3 w-3 mr-1" />}
                    Deactivate
                  </button>
                  <button
                    onClick={() => handleBulkStatusUpdate('unsubscribed')}
                    disabled={bulkActionLoading}
                    className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center disabled:opacity-50"
                  >
                    {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Zap className="h-3 w-3 mr-1" />}
                    Unsubscribe
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    disabled={bulkActionLoading}
                    className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center disabled:opacity-50"
                  >
                    {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Trash2 className="h-3 w-3 mr-1" />}
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Subscribers List */}
          <div className="overflow-hidden sm:rounded-md">
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {subscribers.map((subscriber) => (
                  <li key={subscriber.id} className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className="h-10 w-10 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                            <Mail className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="flex items-center space-x-2">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {subscriber.email}
                            </p>
                            {getStatusBadge(subscriber.status)}
                          </div>
                          {subscriber.name && (
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {subscriber.name}
                            </p>
                          )}
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Subscribed: {formatDate(subscriber.subscribed_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setSelectedSubscriber(subscriber);
                            setEmailForm({ subject: '', content: '' });
                            setShowSendEmail(true);
                          }}
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          <Send className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteSubscriber(subscriber.id)}
                          className="text-red-400 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {/* Add Subscriber Modal */}
      {showAddSubscriber && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Add Subscriber</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                  <input
                    type="email"
                    value={subscriberForm.email}
                    onChange={(e) => setSubscriberForm({...subscriberForm, email: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
                  <input
                    type="text"
                    value={subscriberForm.name}
                    onChange={(e) => setSubscriberForm({...subscriberForm, name: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Notes</label>
                  <textarea
                    value={subscriberForm.notes}
                    onChange={(e) => setSubscriberForm({...subscriberForm, notes: e.target.value})}
                    rows={3}
                    className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowAddSubscriber(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddSubscriber}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  Add Subscriber
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Individual Email Modal (Portal + Enhanced UI) */}
      {showSendEmail && ReactDOM.createPortal(
        (
          <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/50" onClick={() => setShowSendEmail(false)}></div>
            <div className="relative mx-auto mt-8 w-full max-w-5xl bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-800 flex flex-col max-h-[85vh]">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 rounded-t-2xl bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                <div>
                  <h3 className="text-lg font-semibold">Send Email</h3>
                  <p className="text-xs opacity-90">{selectedSubscriber?.email}</p>
                </div>
                <button onClick={() => setShowSendEmail(false)} className="px-3 py-1 rounded-lg bg-white/20 hover:bg-white/30">✕</button>
              </div>

              {/* Body */}
              <div className="px-6 py-5 space-y-4 flex-1 overflow-y-auto">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Subject</label>
                  <input
                    type="text"
                    value={emailForm.subject}
                    onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Message</label>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Use toolbar to format. You can also upload an image.</span>
                    <label className="inline-flex items-center px-3 py-1.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600">
                      Upload Image
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            const res = await uploadAPI.uploadImage(file);
                            const url = res?.data?.data?.url || res?.data?.url || res?.data?.fileUrl;
                            if (!url) throw new Error('No URL returned');
                            setEmailForm(prev => ({ ...prev, content: (prev.content || '') + `<p><img src="${url}" alt="" /></p>` }));
                          } catch (_) {}
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                  <div className="max-h-[60vh] overflow-y-auto rounded-md">
                    <DualEditor
                      value={emailForm.content}
                      onChange={(val) => setEmailForm({ ...emailForm, content: val })}
                      placeholder="Compose your newsletter message..."
                    />
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 flex items-center justify-end gap-3 bg-gray-50 dark:bg-gray-900/50 rounded-b-2xl">
                <button
                  onClick={() => setShowSendEmail(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendIndividualEmail}
                  className="px-5 py-2 text-sm font-semibold text-white rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow"
                >
                  Send Email
                </button>
              </div>
            </div>
          </div>
        ),
        document.body
      )}

      {/* Send Bulk Email Modal */}
      {showBulkEmail && (
        <div className="fixed inset-0 bg-black/50 z-50">
          <div className="relative mx-auto mt-10 w-full max-w-4xl bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-800 p-0 flex flex-col max-h-[85vh]">
            <div className="px-6 py-4 rounded-t-2xl bg-gradient-to-r from-blue-600 to-purple-600 text-white">
              <h3 className="text-lg font-medium">Send Bulk Email</h3>
            </div>
            <div className="px-6 py-5 space-y-4 flex-1 overflow-y-auto">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Subject</label>
                  <input
                    type="text"
                    value={bulkEmailForm.subject}
                    onChange={(e) => setBulkEmailForm({...bulkEmailForm, subject: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Message</label>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Use toolbar to format. You can also upload an image.</span>
                    <label className="inline-flex items-center px-3 py-1.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600">
                      Upload Image
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            const res = await uploadAPI.uploadImage(file);
                            const url = res?.data?.data?.url || res?.data?.url || res?.data?.fileUrl;
                            if (!url) throw new Error('No URL returned');
                            setBulkEmailForm(prev => ({ ...prev, content: (prev.content || '') + `<p><img src="${url}" alt="" /></p>` }));
                          } catch (_) {}
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                  <div className="max-h-[60vh] overflow-y-auto rounded-md">
                    <DualEditor
                      value={bulkEmailForm.content}
                      onChange={(val) => setBulkEmailForm({ ...bulkEmailForm, content: val })}
                      placeholder="Compose your newsletter message..."
                    />
                  </div>
                </div>
            </div>
            <div className="px-6 py-4 flex items-center justify-end gap-3 bg-gray-50 dark:bg-gray-900/50 rounded-b-2xl">
              <button
                onClick={() => setShowBulkEmail(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleSendBulkEmail}
                className="px-5 py-2 text-sm font-semibold text-white rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow"
              >
                Send Bulk Email
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Defaults Modal */}
      {showDefaults && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-6 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-1">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Newsletter Defaults</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Company title</label>
                  <input
                    type="text"
                    value={defaultsForm.companyTitle}
                    onChange={(e) => setDefaultsForm({ ...defaultsForm, companyTitle: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Tagline</label>
                  <input
                    type="text"
                    value={defaultsForm.companyTagline}
                    onChange={(e) => setDefaultsForm({ ...defaultsForm, companyTagline: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Main title</label>
                  <input
                    type="text"
                    value={defaultsForm.mainTitle}
                    onChange={(e) => setDefaultsForm({ ...defaultsForm, mainTitle: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Footer text</label>
                  <input
                    type="text"
                    value={defaultsForm.footerText}
                    onChange={(e) => setDefaultsForm({ ...defaultsForm, footerText: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Footer tagline</label>
                  <input
                    type="text"
                    value={defaultsForm.footerTagline}
                    onChange={(e) => setDefaultsForm({ ...defaultsForm, footerTagline: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Unsubscribe text (shown above footer)</label>
                  <input
                    type="text"
                    value={defaultsForm.unsubscribeText}
                    onChange={(e) => setDefaultsForm({ ...defaultsForm, unsubscribeText: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div className="md:col-span-2">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mt-2">Social links</h4>
                </div>
                {['website','facebook','twitter','linkedin','instagram'].map((key) => (
                  <div key={key} className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">{key} text</label>
                      <input
                        type="text"
                        value={defaultsForm.social[key]?.text || ''}
                        onChange={(e) => setDefaultsForm({ ...defaultsForm, social: { ...defaultsForm.social, [key]: { ...(defaultsForm.social[key] || {}), text: e.target.value } } })}
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">{key} url</label>
                      <input
                        type="url"
                        value={defaultsForm.social[key]?.url || ''}
                        onChange={(e) => setDefaultsForm({ ...defaultsForm, social: { ...defaultsForm.social, [key]: { ...(defaultsForm.social[key] || {}), url: e.target.value } } })}
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowDefaults(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={saveDefaults}
                  disabled={savingDefaults}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {savingDefaults ? 'Saving...' : 'Save Defaults'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsletterManagement;
